import { useLocation } from "react-router-dom";

import { UserDepartment } from "@/api/user/models";
import { DepartmentSlug, SLUG_TO_DEPARTMENT } from "@/types/department";
import { getDepartmentSlugFromPathname } from "@/utils/department-routes";

/**
 * Extrai e valida o department da URL (não do usuário autenticado).
 *
 * Este hook extrai o segmento de department presente na URL atual
 * (ex: em /casting/talentos, extrai "casting") e o valida contra
 * os departments conhecidos do sistema.
 *
 * @returns {Object} Objeto contendo:
 * @returns {UserDepartment | null} department - O department presente na URL, ou null se inválido
 * @returns {DepartmentSlug | null} departmentSlug - Slug do department na URL, ou null se inválido
 * @returns {boolean} isValid - Se o slug na URL é um department válido
 *
 * @example
 * // Em uma rota /casting/talentos
 * const { department, departmentSlug, isValid } = useDepartmentRoute();
 * // department === UserDepartment.CASTING
 * // departmentSlug === "casting"
 * // isValid === true
 *
 * @example
 * // Em uma rota com department inválido /invalid/talentos
 * const { department, departmentSlug, isValid } = useDepartmentRoute();
 * // department === null
 * // departmentSlug === null
 * // isValid === false
 *
 * ⚠️ **Importante**: Este hook retorna o department da URL, não do usuário autenticado.
 */
export function useDepartmentRoute(): {
  department: UserDepartment | null;
  departmentSlug: DepartmentSlug | null;
  isValid: boolean;
} {
  const location = useLocation();
  const departmentSlug = getDepartmentSlugFromPathname(location.pathname);

  if (!departmentSlug) {
    return {
      department: null,
      departmentSlug: null,
      isValid: false,
    };
  }

  const department = SLUG_TO_DEPARTMENT[departmentSlug];

  return {
    department,
    departmentSlug,
    isValid: true,
  };
}
